'use client'

import { useState, useRef, useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Pen, Eraser, Trash2, Eye, EyeOff, Radio, User, Users, UserPen, ChevronDown, Globe, Layers, Camera, Highlighter, Ellipsis, SeparatorHorizontal, StickyNote as StickyNoteIcon, Plus } from 'lucide-react'
import { type PenConfig, type PenType, hueToColor, colorToHue, TRACK_MIN, MIN_PENS, MAX_PENS } from '@/lib/annotations/pens'
import type { SpacerPattern } from '@/types/spacer'
import { cn } from '@/lib/utils'
import { useLayout } from '@/contexts/layout-context'
import { useIsExamPage } from '@/contexts/exam-page-context'
import { createLogger } from '@/lib/logger'
import { useAlertDialog } from '@/hooks/use-alert-dialog'
import { AlertDialogModal } from '@/components/ui/alert-dialog-modal'

const log = createLogger('annotations:toolbar')

// =============================================================================
// TYPES
// =============================================================================

// Types for broadcast controls
export interface ClassOption {
  id: string
  name: string
  hasAnnotationsOnPage?: boolean
}

export interface StudentOption {
  id: string
  displayName: string
  pseudonym?: string
  /** Real email — only present when ClassMembership.identityConsent is true. */
  revealedEmail?: string | null
  hasAnnotationsOnPage?: boolean
}

/**
 * Render label for a student in the annotation UI.
 *
 * If the student consented (`identityConsent: true` on the ClassMembership),
 * the server returns `revealedEmail` — append it after the friendly displayName
 * so the teacher can identify the real student. Otherwise show only the
 * pseudonym-based displayName, preserving the privacy gate.
 */
export function formatStudentLabel(s: { displayName?: string | null; revealedEmail?: string | null; pseudonym?: string }): string {
  const name = s.displayName || s.pseudonym || 'Student'
  return s.revealedEmail ? `${name} (${s.revealedEmail})` : name
}

export type BroadcastMode = 'personal' | 'class' | 'student' | 'page'

// Inline SVG brush icons - use currentColor for automatic light/dark mode support
// Paths extracted from brush_thick.svg and brush_thin.svg with transforms applied
function BrushThickIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 95.1 55.3"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="m 5.28,37.02 c -8.35,-6.1 5.09,-22.53 18.72,-22.1 20.18,0.63 32.97,26.23 53.83,21.66 5.7,-1.25 10.45,-4.36 13.6,-6.76 -10.15,10.24 -19.28,11.66 -25.65,11.64 -15.84,-0.04 -28.81,-10.07 -39.55,-10.07 -6.54,0 -15.92,9.3 -20.95,5.63 z" />
    </svg>
  )
}

function BrushThinIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 95.1 55.3"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="m 176.44,128.54 c -1.61,-2.99 11.93,-12.25 22.02,-12.14 17.74,0.58 33.83,14.77 50.55,14.55 4.74,-0.22 11.01,-1.9 18.49,-8.11 -10.3,9.54 -18.15,9.77 -23.17,9.75 -9.22,-0.04 -33.96,-11.68 -46.1,-11.89 -11.13,-0.19 -20.17,10.83 -21.79,7.84 z" transform="translate(-174.16,-95.58)" />
    </svg>
  )
}

// Small brush indicator for showing annotation status (uses thick brush path)
function BrushIndicator({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 95.1 55.3"
      className={className}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="m 5.28,37.02 c -8.35,-6.1 5.09,-22.53 18.72,-22.1 20.18,0.63 32.97,26.23 53.83,21.66 5.7,-1.25 10.45,-4.36 13.6,-6.76 -10.15,10.24 -19.28,11.66 -25.65,11.64 -15.84,-0.04 -28.81,-10.07 -39.55,-10.07 -6.54,0 -15.92,9.3 -20.95,5.63 z" />
    </svg>
  )
}

// =============================================================================
// TOOLBAR SECTION WRAPPER - provides consistent styling for each section
// =============================================================================

function ToolbarSection({ children, className }: {
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-1', className)}>
      {children}
    </div>
  )
}

function ToolbarDivider() {
  return <div className="w-px h-6 bg-border mx-1" />
}

export type AnnotationMode = 'view' | 'draw' | 'erase' | 'spacer' | 'highlight'

export interface AnnotationLayer {
  id: string
  label: string
  color: string // border color class like 'border-gray-500'
  visible: boolean
  hasContent: boolean
  isActive: boolean // true for the layer being edited
  canDelete: boolean // true if this layer can be cleared
}

interface AnnotationToolbarProps {
  mode: AnnotationMode
  onModeChange: (mode: AnnotationMode) => void
  onClear: () => void
  hasAnnotations: boolean
  pens: PenConfig[]
  activePenId: string
  onPenSelect: (id: string) => void
  onPenColorChange: (id: string, color: string) => void
  onPenSizeChange: (id: string, size: number) => void
  onPenAdd: (type: PenType) => void
  onPenRemove: (id: string) => void
  onPensReorder: (orderedIds: string[]) => void
  /** When broadcasting, highlighter pens are greyed out (highlights are personal). */
  isBroadcasting?: boolean
  onResetZoom: () => void
  // Layers (for students - broadcasted teacher annotations)
  layers?: AnnotationLayer[]
  onLayerToggle?: (layerId: string) => void
  onLayerDelete?: (layerId: string) => void
  // Layer badges visibility (controlled by layers dropdown hover)
  showLayerBadges?: boolean
  onShowLayerBadgesChange?: (show: boolean) => void
  // My annotations controls
  myAnnotationsVisible?: boolean
  myAnnotationsActive?: boolean // True when this is the layer we're drawing to
  onMyAnnotationsToggle?: () => void
  onMyAnnotationsDelete?: () => void
  // Broadcast controls (teachers only)
  isTeacher?: boolean
  // Page author broadcast (public annotations for all visitors)
  isPageAuthor?: boolean
  broadcastToPage?: boolean
  onBroadcastToPageChange?: (value: boolean) => void
  hasPageBroadcastAnnotations?: boolean
  onPageBroadcastDelete?: () => void
  pageBroadcastVisible?: boolean
  onPageBroadcastToggle?: () => void
  // For teachers: visibility/delete of broadcast layers
  classBroadcastVisible?: boolean
  onClassBroadcastToggle?: () => void
  onClassBroadcastDelete?: () => void
  hasClassBroadcastAnnotations?: boolean
  studentFeedbackVisible?: boolean
  onStudentFeedbackToggle?: () => void
  onStudentFeedbackDelete?: () => void
  hasStudentFeedbackAnnotations?: boolean
  classes?: ClassOption[]
  selectedClass?: ClassOption | null
  onClassSelect?: (classData: ClassOption | null) => void
  students?: StudentOption[]
  selectedStudent?: StudentOption | null
  onStudentSelect?: (student: StudentOption | null) => void
  // Last selected student for quick-access (managed by parent)
  lastSelectedStudent?: StudentOption | null
  onClearLastSelectedStudent?: () => void
  // Spacer controls
  spacerPattern?: SpacerPattern
  onSpacerPatternChange?: (pattern: SpacerPattern) => void
  spacerDeleteAnnotations?: boolean
  onSpacerDeleteAnnotationsChange?: (value: boolean) => void
  // Sticky notes controls
  stickyNotePlacementMode?: boolean
  onStickyNotePlacementToggle?: () => void
  stickyNoteCount?: number
}

export function AnnotationToolbar({
  mode,
  onModeChange,
  onClear,
  hasAnnotations,
  pens,
  activePenId,
  onPenSelect,
  onPenColorChange,
  onPenSizeChange,
  onPenAdd,
  onPenRemove,
  onPensReorder,
  isBroadcasting = false,
  onResetZoom,
  layers = [],
  onLayerToggle,
  onLayerDelete,
  // Layer badges visibility
  showLayerBadges = false,
  onShowLayerBadgesChange,
  // My annotations
  myAnnotationsVisible = true,
  myAnnotationsActive = true,
  onMyAnnotationsToggle,
  onMyAnnotationsDelete,
  // Broadcast controls
  isTeacher = false,
  // Page author broadcast
  isPageAuthor = false,
  broadcastToPage = false,
  onBroadcastToPageChange,
  hasPageBroadcastAnnotations = false,
  onPageBroadcastDelete,
  pageBroadcastVisible = true,
  onPageBroadcastToggle,
  // Class broadcast
  classBroadcastVisible = true,
  onClassBroadcastToggle,
  onClassBroadcastDelete,
  hasClassBroadcastAnnotations = false,
  studentFeedbackVisible = true,
  onStudentFeedbackToggle,
  onStudentFeedbackDelete,
  hasStudentFeedbackAnnotations = false,
  classes = [],
  selectedClass = null,
  onClassSelect,
  students = [],
  selectedStudent = null,
  onStudentSelect,
  lastSelectedStudent = null,
  onClearLastSelectedStudent,
  spacerPattern = 'blank',
  onSpacerPatternChange,
  spacerDeleteAnnotations = true,
  onSpacerDeleteAnnotationsChange,
  stickyNotePlacementMode = false,
  onStickyNotePlacementToggle,
  stickyNoteCount = 0,
}: AnnotationToolbarProps) {
  // Get layout context for centering toolbar on page content (not viewport)
  const { sidebarWidth } = useLayout()

  const dialog = useAlertDialog()

  // On exam pages, the ClassToolbar (id="class-toolbar") at the top owns
  // class+student selection; hide the duplicate audience selector here.
  // Default false on non-exam pages.
  const isExamPage = useIsExamPage()

  // Broadcast dropdown state
  const [showClassDropdown, setShowClassDropdown] = useState(false)
  const [showStudentDropdown, setShowStudentDropdown] = useState(false)
  const classDropdownRef = useRef<HTMLDivElement>(null)
  const studentDropdownRef = useRef<HTMLDivElement>(null)

  // Layers dropdown state
  const [showLayersDropdown, setShowLayersDropdown] = useState(false)
  const layersDropdownRef = useRef<HTMLDivElement>(null)

  // My annotations button state (for delete popup)
  const [showMyAnnotationsPopup, setShowMyAnnotationsPopup] = useState(false)
  const myAnnotationsRef = useRef<HTMLDivElement>(null)
  const myAnnotationsHoverTimer = useRef<NodeJS.Timeout | null>(null)
  const myAnnotationsHideTimer = useRef<NodeJS.Timeout | null>(null)
  const myAnnotationsLongPressTimer = useRef<NodeJS.Timeout | null>(null)
  // Save confirm preference to localStorage
  const handleToggleConfirm = (value: boolean) => {
    setConfirmBeforeDelete(value)
    if (typeof window !== 'undefined') {
      localStorage.setItem('annotation-confirm-delete', value.toString())
    }
  }

  // A pen's active mode: highlighters use 'highlight', everything else 'draw'.
  const penMode = (id: string): AnnotationMode =>
    pens.find((p) => p.id === id)?.type === 'highlight' ? 'highlight' : 'draw'

  const handleColorChange = (id: string, color: string) => {
    onPenColorChange(id, color)
    onPenSelect(id)
    const m = penMode(id)
    if (mode !== m) onModeChange(m)
  }

  const handleSizeChange = (id: string, size: number) => {
    onPenSizeChange(id, size)
    onPenSelect(id)
    if (mode !== 'draw') {
      onModeChange('draw')
    }
  }

  // Drag-to-reorder pens (HTML5 drag on the pen button; mouse only — touch
  // keeps the long-press popover). Reorders by id and reports the new order.
  const dragPenId = useRef<string | null>(null)
  const handlePenDrop = (targetId: string) => {
    const from = dragPenId.current
    dragPenId.current = null
    if (!from || from === targetId) return
    const ids = pens.map((p) => p.id)
    const fromIdx = ids.indexOf(from)
    const toIdx = ids.indexOf(targetId)
    if (fromIdx < 0 || toIdx < 0) return
    ids.splice(fromIdx, 1)
    ids.splice(toIdx, 0, from)
    onPensReorder(ids)
  }

  const [showPenControls, setShowPenControls] = useState<string | null>(null)
  const [showAddMenu, setShowAddMenu] = useState(false)
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null)
  const longPressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const longPressStartPos = useRef<{ x: number; y: number } | null>(null)

  const [showDeleteControls, setShowDeleteControls] = useState(false)
  const deleteHoverTimerRef = useRef<NodeJS.Timeout | null>(null)
  const deleteHideTimerRef = useRef<NodeJS.Timeout | null>(null)
  const deleteLongPressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const deleteLongPressStartPos = useRef<{ x: number; y: number } | null>(null)

  const [confirmBeforeDelete, setConfirmBeforeDelete] = useState<boolean>(() => {
    // Load preference from localStorage - default is false (no popup)
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('annotation-confirm-delete')
      if (saved !== null) {
        return saved === 'true'
      }
    }
    return false
  })

  // "More tools" popover state
  const [showMoreTools, setShowMoreTools] = useState(false)
  const moreToolsRef = useRef<HTMLDivElement>(null)

  // Spacer pattern picker state (hover/long-press popover, same pattern as pen)

  // Ref for the popover elements to detect clicks outside
  const penPopoverRef = useRef<HTMLDivElement>(null)
  const deletePopoverRef = useRef<HTMLDivElement>(null)

  // Close popovers when stylus touches paper or when clicking outside
  useEffect(() => {
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Element

      // Close all popovers on stylus input on paper/canvas (user wants to draw)
      if (e.pointerType === 'pen') {
        // Only close if touching paper/canvas area, not the toolbar
        const isOnToolbar = target.closest('[data-annotation-toolbar]')
        if (!isOnToolbar) {
          setShowPenControls(null)
          setShowDeleteControls(false)
        }
        return
      }

      // For touch/mouse, close if clicking outside the popover and toolbar
      const isInsideToolbar = target.closest('[data-annotation-toolbar]')
      if (isInsideToolbar) return // Don't close for any toolbar interaction

      // Clicking outside toolbar closes all popovers
      if (showPenControls !== null) {
        setShowPenControls(null)
      }
      if (showDeleteControls) {
        setShowDeleteControls(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => document.removeEventListener('pointerdown', handlePointerDown, true)
  }, [showPenControls, showDeleteControls])

  const handlePenMouseEnter = (id: string) => {
    // Clear any pending hide timer
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current)
      hideTimerRef.current = null
    }

    // Set timer to show pen controls
    hoverTimerRef.current = setTimeout(() => {
      setShowPenControls(id)
    }, 300)
  }

  const handlePenMouseLeave = () => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }

    // If pen controls are showing, delay hiding them to give user time to move into them
    if (showPenControls !== null) {
      hideTimerRef.current = setTimeout(() => {
        setShowPenControls(null)
      }, 200)
    }
  }

  const handlePenClick = (id: string) => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
    setShowPenControls(null)

    // Highlights are personal — highlighter pens are inert while broadcasting.
    if (isBroadcasting && pens.find((p) => p.id === id)?.type === 'highlight') return

    // Clicking the active pen (draw or highlight) deactivates it.
    const isActive = (mode === 'draw' || mode === 'highlight') && activePenId === id
    if (isActive) {
      onModeChange('view')
    } else {
      // Switch to this pen and enter its mode (highlighters → 'highlight').
      onPenSelect(id)
      onModeChange(penMode(id))
    }
  }

  // Long-press handlers for pen tools (stylus/touch support)
  const handlePenPointerDown = (e: React.PointerEvent, id: string) => {
    // Only handle touch/pen, not mouse (mouse uses hover)
    if (e.pointerType === 'mouse') return

    // Prevent default to avoid text selection on long-press (iOS Safari)
    e.preventDefault()

    longPressStartPos.current = { x: e.clientX, y: e.clientY }
    longPressTimerRef.current = setTimeout(() => {
      setShowPenControls(id)
      // Also select this pen when opening its config
      onPenSelect(id)
      const m = penMode(id)
      if (mode !== m) onModeChange(m)
      longPressTimerRef.current = null
    }, 500)
  }

  const handlePenPointerMove = (e: React.PointerEvent) => {
    if (!longPressStartPos.current || !longPressTimerRef.current) return

    const dx = e.clientX - longPressStartPos.current.x
    const dy = e.clientY - longPressStartPos.current.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    // Cancel long-press if moved more than 10px
    if (distance > 10) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
      longPressStartPos.current = null
    }
  }

  const handlePenPointerUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }
    longPressStartPos.current = null
  }

  const handleEraserClick = () => {
    onModeChange(mode === 'erase' ? 'view' : 'erase')
  }

  const handleDeleteMouseEnter = () => {
    // Clear any pending hide timer
    if (deleteHideTimerRef.current) {
      clearTimeout(deleteHideTimerRef.current)
      deleteHideTimerRef.current = null
    }

    // Set timer to show delete controls
    deleteHoverTimerRef.current = setTimeout(() => {
      setShowDeleteControls(true)
    }, 300)
  }

  const handleDeleteMouseLeave = () => {
    if (deleteHoverTimerRef.current) {
      clearTimeout(deleteHoverTimerRef.current)
      deleteHoverTimerRef.current = null
    }

    // If delete controls are showing, delay hiding them
    if (showDeleteControls) {
      deleteHideTimerRef.current = setTimeout(() => {
        setShowDeleteControls(false)
      }, 200)
    }
  }

  const handleDeleteClick = () => {
    if (deleteHoverTimerRef.current) {
      clearTimeout(deleteHoverTimerRef.current)
      deleteHoverTimerRef.current = null
    }
    setShowDeleteControls(false)

    if (confirmBeforeDelete) {
      dialog.showConfirm('Clear all annotations on this page?', () => {
        onClear()
      }, { destructive: true, title: 'Clear annotations', confirmText: 'Clear' })
    } else {
      onClear()
    }
  }

  // Long-press handlers for delete button (stylus/touch support)
  const handleDeletePointerDown = (e: React.PointerEvent) => {
    // Only handle touch/pen, not mouse (mouse uses hover)
    if (e.pointerType === 'mouse') return

    // Prevent default to avoid text selection on long-press (iOS Safari)
    e.preventDefault()

    deleteLongPressStartPos.current = { x: e.clientX, y: e.clientY }
    deleteLongPressTimerRef.current = setTimeout(() => {
      setShowDeleteControls(true)
      deleteLongPressTimerRef.current = null
    }, 500)
  }

  const handleDeletePointerMove = (e: React.PointerEvent) => {
    if (!deleteLongPressStartPos.current || !deleteLongPressTimerRef.current) return

    const dx = e.clientX - deleteLongPressStartPos.current.x
    const dy = e.clientY - deleteLongPressStartPos.current.y
    const distance = Math.sqrt(dx * dx + dy * dy)

    // Cancel long-press if moved more than 10px
    if (distance > 10) {
      clearTimeout(deleteLongPressTimerRef.current)
      deleteLongPressTimerRef.current = null
      deleteLongPressStartPos.current = null
    }
  }

  const handleDeletePointerUp = () => {
    if (deleteLongPressTimerRef.current) {
      clearTimeout(deleteLongPressTimerRef.current)
      deleteLongPressTimerRef.current = null
    }
    deleteLongPressStartPos.current = null
  }

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (classDropdownRef.current && !classDropdownRef.current.contains(e.target as Node)) {
        setShowClassDropdown(false)
      }
      if (studentDropdownRef.current && !studentDropdownRef.current.contains(e.target as Node)) {
        setShowStudentDropdown(false)
      }
      if (myAnnotationsRef.current && !myAnnotationsRef.current.contains(e.target as Node)) {
        setShowMyAnnotationsPopup(false)
      }
      if (layersDropdownRef.current && !layersDropdownRef.current.contains(e.target as Node)) {
        setShowLayersDropdown(false)
        onShowLayerBadgesChange?.(false)
      }
if (moreToolsRef.current && !moreToolsRef.current.contains(e.target as Node)) {
        setShowMoreTools(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onShowLayerBadgesChange])

  // My annotations button hover/long-press handlers
  const handleMyAnnotationsMouseEnter = () => {
    if (myAnnotationsHideTimer.current) {
      clearTimeout(myAnnotationsHideTimer.current)
      myAnnotationsHideTimer.current = null
    }
    myAnnotationsHoverTimer.current = setTimeout(() => {
      log('Showing my annotations popup', { hasAnnotations })
      setShowMyAnnotationsPopup(true)
    }, 400)
  }

  const handleMyAnnotationsMouseLeave = () => {
    if (myAnnotationsHoverTimer.current) {
      clearTimeout(myAnnotationsHoverTimer.current)
      myAnnotationsHoverTimer.current = null
    }
    if (showMyAnnotationsPopup) {
      myAnnotationsHideTimer.current = setTimeout(() => {
        setShowMyAnnotationsPopup(false)
      }, 200)
    }
  }

  const handleMyAnnotationsPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return
    e.preventDefault()
    myAnnotationsLongPressTimer.current = setTimeout(() => {
      setShowMyAnnotationsPopup(true)
      myAnnotationsLongPressTimer.current = null
    }, 500)
  }

  const handleMyAnnotationsPointerUp = () => {
    if (myAnnotationsLongPressTimer.current) {
      clearTimeout(myAnnotationsLongPressTimer.current)
      myAnnotationsLongPressTimer.current = null
    }
  }

  const toolbarContent = (
    <>
    <div
      id="annotation-toolbar"
      data-annotation-toolbar
      className="fixed bottom-6 z-50 select-none flex items-end"
      style={{ left: `calc(${sidebarWidth}px + (100% - ${sidebarWidth}px) / 2)`, transform: 'translateX(-50%)', isolation: 'isolate', touchAction: 'manipulation' }}
      onMouseEnter={() => onShowLayerBadgesChange?.(true)}
      onMouseLeave={() => onShowLayerBadgesChange?.(false)}
    >
      {/* Single horizontal toolbar */}
      <div className="bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg p-2 flex items-center gap-1">

        {/* Audience selection + per-broadcast management (Public/class/student
            eye+trash) all moved up to the ClassToolbar (id="class-toolbar")
            in the sidebar. The floating bar holds only drawing tools and the
            personal "my annotations" toggle now. */}

        {/* ============ SECTION 2: Layer Controls (Students - broadcasted teacher layers) ============ */}
        {/* Layers dropdown button - hovering shows labels on annotations */}
        {!isTeacher && layers.filter(l => !l.isActive).length > 0 && (
          <>
            <ToolbarSection>
              <div className="relative" ref={layersDropdownRef}>
                <button
                  onClick={() => {
                    const newState = !showLayersDropdown
                    setShowLayersDropdown(newState)
                    onShowLayerBadgesChange?.(newState)
                  }}
                  className={cn(
                    'p-2 rounded-md transition-colors flex items-center gap-1',
                    showLayersDropdown
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                  )}
                  title="View layers"
                >
                  <Layers className="w-4 h-4" />
                  <ChevronDown className="w-3 h-3" />
                </button>

                {/* Layers dropdown menu */}
                {showLayersDropdown && (
                  <div
                    className="absolute bottom-full mb-2 left-0 bg-background border border-border rounded-lg shadow-lg py-1 min-w-[180px]"
                  >
                    {layers.filter(l => !l.isActive).map(layer => {
                      // Map layer color to badge color class
                      const colorClass = layer.color.includes('blue') ? 'layers-menu-blue'
                        : layer.color.includes('orange') ? 'layers-menu-orange'
                        : layer.color.includes('green') ? 'layers-menu-green'
                        : layer.color.includes('purple') ? 'layers-menu-purple'
                        : ''

                      return (
                        <div
                          key={layer.id}
                          className={cn(
                            'flex items-center gap-2 px-3 py-1.5 text-sm transition-colors',
                            colorClass,
                            !layer.visible && 'opacity-50'
                          )}
                        >
                          <button
                            onClick={() => onLayerToggle?.(layer.id)}
                            className="p-0.5 rounded hover:bg-accent/50 transition-colors"
                            title={layer.visible ? `Hide ${layer.label}` : `Show ${layer.label}`}
                          >
                            {layer.visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                          </button>
                          <span className="flex-1 truncate">{layer.label}</span>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </ToolbarSection>
            <ToolbarDivider />
          </>
        )}

        {/* ============ SECTION 3: My Annotations Button (Everyone) ============ */}
        <ToolbarSection>
          <div className="relative" ref={myAnnotationsRef}>
            <button
              onClick={onMyAnnotationsToggle}
              onMouseEnter={handleMyAnnotationsMouseEnter}
              onMouseLeave={handleMyAnnotationsMouseLeave}
              onPointerDown={handleMyAnnotationsPointerDown}
              onPointerUp={handleMyAnnotationsPointerUp}
              onPointerCancel={handleMyAnnotationsPointerUp}
              className={cn(
                'p-2 rounded-md transition-colors relative',
                myAnnotationsActive && myAnnotationsVisible
                  ? 'bg-primary text-primary-foreground'
                  : myAnnotationsVisible
                    ? 'text-foreground hover:bg-accent'
                    : 'text-muted-foreground/50 hover:text-muted-foreground hover:bg-accent'
              )}
              title={myAnnotationsActive
                ? (myAnnotationsVisible ? 'Hide my annotations' : 'Show my annotations')
                : (myAnnotationsVisible ? 'Hide my annotations' : 'Show my annotations')
              }
            >
              <UserPen className="w-4 h-4" />
              {/* Visibility indicator - show when not active, OR when active but hidden */}
              {(!myAnnotationsActive || !myAnnotationsVisible) && (
                <span className="absolute -top-0.5 -right-0.5">
                  {myAnnotationsVisible
                    ? <Eye className="w-2.5 h-2.5 text-foreground" />
                    : <EyeOff className="w-2.5 h-2.5 text-muted-foreground" />
                  }
                </span>
              )}
            </button>

            {/* Delete popup on hover/long-press */}
            {showMyAnnotationsPopup && hasAnnotations && (
              <div
                className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-background border border-border rounded-lg shadow-lg p-2"
                onMouseEnter={() => {
                  if (myAnnotationsHideTimer.current) {
                    clearTimeout(myAnnotationsHideTimer.current)
                    myAnnotationsHideTimer.current = null
                  }
                }}
                onMouseLeave={() => setShowMyAnnotationsPopup(false)}
              >
                <button
                  onClick={() => {
                    log('Delete button clicked, calling onMyAnnotationsDelete')
                    onMyAnnotationsDelete?.()
                    setShowMyAnnotationsPopup(false)
                  }}
                  className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                  title="Clear"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </ToolbarSection>
        <ToolbarDivider />

        {/* ============ SECTION 4: Drawing Tools ============ */}
        <ToolbarSection>
          {/* Modular pens — add/remove/reorder; persisted to localStorage. */}
          {pens.map((pen, penIndex) => (
            <div key={pen.id} className="relative">
              <button
                data-pen-button
                draggable
                onDragStart={() => { dragPenId.current = pen.id }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handlePenDrop(pen.id)}
                onClick={() => handlePenClick(pen.id)}
                onMouseEnter={() => handlePenMouseEnter(pen.id)}
                onMouseLeave={handlePenMouseLeave}
                onPointerDown={(e) => handlePenPointerDown(e, pen.id)}
                onPointerMove={handlePenPointerMove}
                onPointerUp={handlePenPointerUp}
                onPointerCancel={handlePenPointerUp}
                className={cn(
                  'p-2 rounded-md transition-colors relative',
                  (mode === 'draw' || mode === 'highlight') && activePenId === pen.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                  // Highlights are personal — greyed out while broadcasting.
                  isBroadcasting && pen.type === 'highlight' && 'opacity-40 cursor-not-allowed hover:bg-transparent hover:text-muted-foreground'
                )}
                title={pen.type === 'highlight'
                  ? (isBroadcasting ? "Highlights are always personal — can't be broadcast" : `Highlighter ${penIndex + 1}`)
                  : `Pen ${penIndex + 1}`}
                aria-label={pen.type === 'highlight' ? `Select highlighter ${penIndex + 1}` : `Select pen ${penIndex + 1}`}
              >
                {pen.type === 'highlight' ? <Highlighter className="w-4 h-4" /> : <Pen className="w-4 h-4" />}
                {/* Color indicator. `color` pins the theme-ink swatch: a pen
                    stored as `currentColor` shows the true foreground (and
                    flips with the theme); explicit colours ignore it. */}
                <div
                  className="annotation-color-indicator absolute bottom-0.5 right-0.5 w-2.5 h-2.5 rounded-full border border-white dark:border-gray-800"
                  style={{ backgroundColor: pen.color, color: 'hsl(var(--foreground))' }}
                />
              </button>

              {/* Pen controls popover. Pens: size + hue sliders. Highlighters:
                  hue slider only. Both: a remove bin. */}
              {showPenControls === pen.id && (
                <div
                  ref={penPopoverRef}
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex gap-2"
                  onMouseEnter={() => {
                    if (hoverTimerRef.current) {
                      clearTimeout(hoverTimerRef.current)
                    }
                    if (hideTimerRef.current) {
                      clearTimeout(hideTimerRef.current)
                      hideTimerRef.current = null
                    }
                  }}
                  onMouseLeave={() => setShowPenControls(null)}
                >
                  {/* Size slider — pens only */}
                  {pen.type !== 'highlight' && (
                    <div className="bg-background border border-border rounded-lg shadow-lg p-3 flex flex-col items-center gap-3 min-h-[200px]">
                      <BrushThickIcon className="w-6 h-6 shrink-0 opacity-60" />
                      <input
                        type="range"
                        min="0.1"
                        max="5"
                        step="0.1"
                        value={pen.size}
                        onChange={(e) => handleSizeChange(pen.id, parseFloat(e.target.value))}
                        className="grow cursor-pointer [writing-mode:vertical-lr] [direction:rtl] slider-vertical"
                      />
                      <BrushThinIcon className="w-6 h-6 shrink-0 opacity-60" />
                    </div>
                  )}

                  {/* Hue slider (rainbow) + remove */}
                  <div className="bg-background border border-border rounded-lg shadow-lg p-3 flex flex-col items-center gap-3 min-h-[200px]">
                    <input
                      type="range"
                      min={TRACK_MIN}
                      max="360"
                      step="1"
                      value={colorToHue(pen.color)}
                      onChange={(e) => handleColorChange(pen.id, hueToColor(parseFloat(e.target.value)))}
                      aria-label="Pen colour"
                      // Thumb previews the selected colour. `color` resolves an
                      // ink pen's `currentColor` thumb to the theme foreground.
                      style={{ '--thumb-color': pen.color, color: 'hsl(var(--foreground))' } as React.CSSProperties}
                      className="grow cursor-pointer [writing-mode:vertical-lr] [direction:rtl] slider-vertical hue-slider"
                    />
                    {pens.length > MIN_PENS && (
                      <button
                        onClick={() => onPenRemove(pen.id)}
                        title={pen.type === 'highlight' ? 'Remove highlighter' : 'Remove pen'}
                        aria-label="Remove pen"
                        className="text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Eraser Tool */}
          <button
            onClick={handleEraserClick}
            className={cn(
              'p-2 rounded-md transition-colors',
              mode === 'erase'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            title="Erase"
          >
            <Eraser className="w-4 h-4" />
          </button>

          {/* Add pen / highlighter */}
          {pens.length < MAX_PENS && (
            <div className="relative">
              <button
                onClick={() => setShowAddMenu((v) => !v)}
                title="Add pen or highlighter"
                aria-label="Add pen or highlighter"
                className={cn(
                  'p-2 rounded-md transition-colors',
                  showAddMenu
                    ? 'bg-accent text-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
              >
                <Plus className="w-4 h-4" />
              </button>
              {showAddMenu && (
                <div
                  className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 flex flex-col gap-1 rounded-lg border border-border bg-background p-1 shadow-lg"
                  onMouseLeave={() => setShowAddMenu(false)}
                >
                  <button
                    onClick={() => { onPenAdd('highlight'); setShowAddMenu(false) }}
                    className="flex items-center gap-2 rounded px-2 py-1 text-sm text-foreground hover:bg-accent"
                  >
                    <Highlighter className="w-4 h-4" /> Highlighter
                  </button>
                  <button
                    onClick={() => { onPenAdd('pen'); setShowAddMenu(false) }}
                    className="flex items-center gap-2 rounded px-2 py-1 text-sm text-foreground hover:bg-accent"
                  >
                    <Pen className="w-4 h-4" /> Pen
                  </button>
                </div>
              )}
            </div>
          )}

          <ToolbarDivider />

          {/* Spacer tool - insert visual spacers between content blocks */}
          <button
            className={cn(
              'p-2 rounded-md transition-colors',
              mode === 'spacer'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            )}
            onClick={() => {
              onModeChange(mode === 'spacer' ? 'view' : 'spacer')
            }}
            title="Insert spacer"
          >
            <SeparatorHorizontal className="w-4 h-4" />
          </button>

          {/* Sticky Note tool */}
          <div className="relative">
            <button
              className={cn(
                'p-2 rounded-md transition-colors relative',
                stickyNotePlacementMode
                  ? 'bg-yellow-400 dark:bg-yellow-500 text-yellow-950'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              onClick={onStickyNotePlacementToggle}
              title={stickyNotePlacementMode ? 'Cancel sticky note placement (Esc)' : 'Add sticky note'}
            >
              <StickyNoteIcon className="w-4 h-4" />
              {stickyNoteCount > 0 && !stickyNotePlacementMode && (
                <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-yellow-400 dark:bg-yellow-500 text-yellow-950 text-[9px] font-bold flex items-center justify-center px-0.5 leading-none tabular-nums">
                  {stickyNoteCount > 99 ? '99+' : stickyNoteCount}
                </span>
              )}
            </button>
          </div>

          {/* More tools - popover with snap and highlight explanations */}
          <div className="relative" ref={moreToolsRef}>
            <button
              className={cn(
                'p-2 rounded-md transition-colors',
                showMoreTools
                  ? 'bg-accent text-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              )}
              onClick={() => setShowMoreTools(!showMoreTools)}
              title="More tools"
            >
              <Ellipsis className="w-4 h-4" />
            </button>
            {showMoreTools && (
              <div className="absolute bottom-full mb-2 left-0 z-50">
                <div className="bg-popover text-popover-foreground text-sm rounded-md shadow-lg border border-border p-2 w-64 space-y-2">
                  {/* Snap explanation */}
                  <div className="flex items-start gap-2.5 px-1 py-1">
                    <Camera className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div>
                      <span className="text-foreground font-medium">Snap</span>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Paste and crop a screenshot
                        <br />
                        <span className="inline-flex items-baseline gap-0.5 mt-0.5">
                          <kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono border border-border shadow-xs">{typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl'}</kbd>
                          <span className="text-muted-foreground mx-0.5">+</span>
                          <kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono border border-border shadow-xs">V</kbd>
                        </span>
                      </p>
                    </div>
                  </div>
                  {/* Highlight explanation */}
                  <div className="flex items-start gap-2.5 px-1 py-1">
                    <Highlighter className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                    <div>
                      <span className="text-foreground font-medium">Highlight</span>
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                        Select text to highlight
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

        </ToolbarSection>
      </div>
      {/* Slot for sibling pills (e.g. the slide "Present" button) so they flow
          inline with the toolbar and reflow with the browser — no JS positioning.
          Empty (0-width) when unused, so the toolbar stays centred on its own. */}
      <div id="annotation-toolbar-aside" className="flex items-end" />
    </div>
    <AlertDialogModal
      open={dialog.open} onOpenChange={dialog.setOpen}
      type={dialog.type} title={dialog.title} message={dialog.message}
      onConfirm={dialog.onConfirm} showCancel={dialog.showCancel}
      confirmText={dialog.confirmText} cancelText={dialog.cancelText}
      destructive={dialog.destructive}
    />
    </>
  )

  // Render to document.body to avoid zoom transforms
  return typeof window !== 'undefined' ? createPortal(toolbarContent, document.body) : toolbarContent
}
